import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function PaymentSuccess() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const orderCode = searchParams.get('orderCode');
    const [status, setStatus] = useState<'checking' | 'paid' | 'pending' | 'error'>('checking');
    const [planLabel, setPlanLabel] = useState('');
    const [dots, setDots] = useState('');

    const API = import.meta.env.VITE_API_URL || '/api';
    const H = { Authorization: `Bearer ${localStorage.getItem('auth_token')}`, Accept: 'application/json' };

    useEffect(() => {
        if (!orderCode) { navigate('/billing'); return; }

        // Animated dots
        const dotInterval = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);

        // Poll payment status (PayOS webhook có thể chậm vài giây)
        let attempts = 0;
        const poll = setInterval(async () => {
            attempts++;
            try {
                const res  = await fetch(`${API}/admin/billing/payment-status/${orderCode}`, { headers: H });
                const data = await res.json();

                if (data.status === 'paid') {
                    clearInterval(poll);
                    clearInterval(dotInterval);
                    setPlanLabel(data.plan?.plan_label ?? 'VIP');
                    setStatus('paid');
                    return;
                }

                if (data.status === 'cancelled' || data.status === 'expired' || attempts >= 30) {
                    clearInterval(poll);
                    clearInterval(dotInterval);
                    setStatus(attempts >= 30 ? 'pending' : 'error');
                }
            } catch {
                if (attempts >= 30) { clearInterval(poll); clearInterval(dotInterval); setStatus('error'); }
            }
        }, 2000); // Check every 2 seconds

        return () => { clearInterval(poll); clearInterval(dotInterval); };
    }, [orderCode]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center">
                {status === 'checking' && (
                    <>
                        <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
                            <svg className="w-10 h-10 text-blue-500 animate-spin" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Đang xác nhận{dots}</h2>
                        <p className="text-gray-500">Vui lòng chờ trong giây lát, hệ thống đang xác nhận thanh toán của bạn.</p>
                    </>
                )}

                {status === 'paid' && (
                    <>
                        <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                            <span className="text-5xl">🎉</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Kích hoạt thành công!</h2>
                        <p className="text-gray-500 mb-6">Gói <strong>{planLabel}</strong> của bạn đã được kích hoạt. Tận hưởng toàn bộ tính năng ngay bây giờ!</p>
                        <div className="flex flex-col gap-3">
                            <button onClick={() => navigate('/campaigns/new')} className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700">
                                Tạo Campaign ngay
                            </button>
                            <button onClick={() => navigate('/billing')} className="w-full text-gray-500 hover:text-gray-700 text-sm">
                                Xem trang gói dịch vụ
                            </button>
                        </div>
                    </>
                )}

                {status === 'pending' && (
                    <>
                        <div className="w-20 h-20 mx-auto mb-6 bg-yellow-100 rounded-full flex items-center justify-center">
                            <span className="text-5xl">⏳</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Đang xử lý</h2>
                        <p className="text-gray-500 mb-6">Thanh toán đang được xác nhận. Gói sẽ được kích hoạt trong vài phút. Vui lòng kiểm tra lại sau.</p>
                        <button onClick={() => navigate('/billing')} className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700">
                            Về trang Gói dịch vụ
                        </button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                            <span className="text-5xl">❌</span>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Thanh toán thất bại</h2>
                        <p className="text-gray-500 mb-6">Đã có lỗi xảy ra. Vui lòng thử lại hoặc liên hệ hỗ trợ.</p>
                        <button onClick={() => navigate('/billing')} className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700">
                            Thử lại
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
